/**
 * Teams Page Skeleton
 * Simplified loading state for the teams/team chat page
 *
 * Layout:
 * - Left: Team Sidebar (w-80)
 *   - Header: title + new message button
 *   - Sections: Channels + Direct Messages (title + avatar rows)
 * - Right: Chat area (flex-1)
 *   - Header with avatar + name + action buttons
 *   - Message bubbles
 *   - Input area
 */

import React from 'react';
import { Skeleton, SkeletonCircle } from './Skeleton';

/**
 * Team channel/conversation row skeleton
 * Matches ChannelsSection and DirectMessagesSection item structure
 * - w-full px-2 py-2 rounded-md flex items-center
 * - Avatar: w-10 h-10 rounded-full
 */
const TeamRowSkeleton: React.FC = () => (
  <div className="w-full px-2 py-2 rounded-md flex items-center">
    <SkeletonCircle className="w-10 h-10 flex-shrink-0 mr-2" />
    <Skeleton className="h-4 flex-1" />
  </div>
);

/**
 * Sidebar section skeleton
 * Simplified section with just title and items
 */
const SidebarSectionSkeleton: React.FC<{
  titleWidth: string;
  itemCount: number;
  className?: string;
}> = ({ titleWidth, itemCount, className }) => (
  <div className={className}>
    <div className="px-2 mb-2">
      <Skeleton className={`h-5 ${titleWidth}`} />
    </div>
    <div className="space-y-1.5">
      {Array.from({ length: itemCount }).map((_, i) => (
        <TeamRowSkeleton key={i} />
      ))}
    </div>
  </div>
);

/**
 * Message bubble skeleton for team chat
 * Simplified to match TeamChatMessageBubble structure
 */
const TeamMessageSkeleton: React.FC<{ isOwn?: boolean; width?: string }> = ({
  isOwn,
  width = isOwn ? 'w-32' : 'w-44'
}) => (
  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
    <div className={`flex items-start gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
      {!isOwn && <SkeletonCircle className="w-8 h-8 flex-shrink-0" />}
      <Skeleton className={`h-9 ${width} rounded-2xl ${isOwn ? 'rounded-tr-sm' : 'rounded-tl-sm'}`} />
    </div>
  </div>
);

/**
 * Chat header skeleton
 * Matches ChannelHeader/DirectMessageHeader structure
 * - py-3 px-3 min-h-[44px]
 * - Avatar (w-10 h-10) + name (font-semibold text-xl) + status
 * - Action buttons (p-1.5 each): Search, Pin, Panel toggle
 */
const ChatHeaderSkeleton: React.FC = () => (
  <div className="flex items-center py-3 px-3 min-h-[44px]">
    <div className="flex items-center min-w-0 w-full justify-between">
      {/* Left side: Avatar + name + status */}
      <div className="flex items-center min-w-0">
        <SkeletonCircle className="w-10 h-10 mr-3" />
        <div className="flex flex-col justify-center min-w-0 mr-2">
          <Skeleton className="h-6 w-32 mb-1" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
      {/* Right side: Action buttons */}
      <div className="flex items-center gap-1">
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
    </div>
  </div>
);

/**
 * Full teams page skeleton
 * Use this as the loading state for the teams page
 */
export const TeamsSkeleton: React.FC = () => (
  <div className="h-full w-full flex overflow-hidden rounded-2xl">
    {/* Sidebar - matches TeamSidebar structure */}
    <div
      className="w-80 h-full flex flex-col p-4 border-r flex-shrink-0"
      style={{ backgroundColor: 'var(--color-card-bg)', borderColor: 'var(--color-border)' }}
    >
      {/* Header - matches TeamSidebar header */}
      <div className="flex items-center justify-between pb-3">
        {/* Title: "Team Chat" (text-2xl font-semibold) */}
        <Skeleton className="h-8 w-32" />
        {/* New message button (p-1.5 with 18px icon = ~30px) */}
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>

      {/* Scrollable content - matches TeamSidebar sections */}
      <div className="flex-1 overflow-y-auto mt-4">
        {/* Channels section */}
        <SidebarSectionSkeleton
          titleWidth="w-20"
          itemCount={3}
          className="mb-6"
        />

        {/* Direct Messages section */}
        <SidebarSectionSkeleton
          titleWidth="w-32"
          itemCount={4}
        />
      </div>
    </div>

    {/* Chat area - matches TeamChatView structure */}
    <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0 relative bg-background">
      {/* Header - matches ChannelHeader/DirectMessageHeader */}
      <ChatHeaderSkeleton />

      {/* Messages area */}
      <div className="flex-1 overflow-hidden p-4">
        <TeamMessageSkeleton width="w-36" />
        <TeamMessageSkeleton isOwn width="w-28" />
        <TeamMessageSkeleton width="w-52" />
        <TeamMessageSkeleton isOwn width="w-32" />
        <TeamMessageSkeleton width="w-40" />
      </div>

      {/* Input area - matches MessageHandling input */}
      <div className="p-4 bg-card-background">
        <Skeleton className="h-12 w-full rounded-xl" />
      </div>
    </div>
  </div>
);

/**
 * Sidebar-only skeleton
 * Use this when only the team sidebar needs a loading state
 */
export const TeamSidebarSkeleton: React.FC = () => (
  <div
    className="w-80 h-full flex flex-col p-4 border-r flex-shrink-0"
    style={{ backgroundColor: 'var(--color-card-bg)', borderColor: 'var(--color-border)' }}
  >
    <div className="flex items-center justify-between pb-3">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-9 w-9 rounded-md" />
    </div>
    <div className="flex-1 overflow-y-auto mt-4">
      <SidebarSectionSkeleton titleWidth="w-20" itemCount={3} className="mb-6" />
      <SidebarSectionSkeleton titleWidth="w-32" itemCount={4} />
    </div>
  </div>
);

/**
 * Chat-only skeleton
 * Use this when only the chat area needs a loading state
 */
export const TeamChatSkeleton: React.FC = () => (
  <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0 bg-background">
    <ChatHeaderSkeleton />
    <div className="flex-1 overflow-hidden p-4">
      <TeamMessageSkeleton width="w-36" />
      <TeamMessageSkeleton isOwn width="w-28" />
      <TeamMessageSkeleton width="w-52" />
      <TeamMessageSkeleton isOwn width="w-32" />
      <TeamMessageSkeleton width="w-40" />
    </div>
    <div className="p-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
      <Skeleton className="h-12 w-full rounded-xl" />
    </div>
  </div>
);

export default TeamsSkeleton;
